import {TextInput} from '@/components/atoms';
import PropTypes from 'prop-types';
import {preventInvalidNumericInput} from '@/utils/inputValidations';
import {Constants} from '@/utils/constants/commonConstants';

/**
 * InventoryFieldsRowForCardTypeBoth component - Reusable component for quota, leftover inventory, and limit per store fields
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing quota, leftoverInventory, limitPerStore
 * @param {Function} props.handleQuotaDigitalCardChange - Function to handle quota digital card field changes
 * @param {Function} props.handleLeftOverInventoryDigitalCardChange - Function to handle leftover inventory digital card field changes
 * @param {Function} props.handleLimitPerStoreDigitalCardChange - Function to handle limit per store digital card field changes
 * @param {Function} props.handleQuotaPhysicalCardChange - Function to handle quota physical card field changes
 * @param {Function} props.handleLeftOverInventoryPhysicalCardChange - Function to handle leftover inventory physical card field changes
 * @param {Function} props.handleLimitPerStorePhysicalCardChange - Function to handle limit per store physical card field changes
 * @param {boolean} props.isQuotaDigitalCardDisabled - Whether quota digital card field should be disabled
 * @param {boolean} props.isQuotaPhysicalCardDisabled - Whether quota physical card field should be disabled
 * @param {string} props.quotaDigitalCardValidationError - Validation error message for quota digital card field
 * @param {string} props.quotaPhysicalCardValidationError - Validation error message for quota physical card field
 * @param {string} props.limitPerStoreDigitalCardValidationError - Validation error message for limit per store digital card field
 * @param {string} props.limitPerStorePhysicalCardValidationError - Validation error message for limit per store physical card field
 * @returns {JSX.Element} The rendered InventoryFieldsRowForCardTypeBoth component
 */

const renderTextInput = ({
  id,
  label,
  value,
  onChange,
  placeholderText,
  className = 'text-sm',
  isDark = true,
  type = 'text',
  disabled = false,
  handleKeyDown = preventInvalidNumericInput,
  maxLength,
  error
}) => (
  <div className='w-full md:w-[262px]'>
    <TextInput
      id={id}
      label={label}
      value={value}
      onChange={onChange}
      placeholderText={placeholderText}
      className={className}
      isDark={isDark}
      type={type}
      disabled={disabled}
      handleKeyDown={handleKeyDown}
      maxLength={maxLength}
      error={error}
    />
  </div>
);

const InventoryFieldsRowForCardTypeBoth = ({
  formData,
  handleQuotaDigitalCardChange,
  handleLeftOverInventoryDigitalCardChange,
  handleLimitPerStoreDigitalCardChange,
  handleQuotaPhysicalCardChange,
  handleLeftOverInventoryPhysicalCardChange,
  handleLimitPerStorePhysicalCardChange,
  isQuotaDigitalCardDisabled,
  isQuotaPhysicalCardDisabled,
  quotaDigitalCardValidationError,
  quotaPhysicalCardValidationError,
  limitPerStoreDigitalCardValidationError,
  limitPerStorePhysicalCardValidationError
}) => {
  return (
    <div>
      <div className='flex flex-col items-start gap-6 md:flex-row'>
        {renderTextInput({
          id: 'quota-digital-card',
          label: 'Quota Digital Card*',
          value: formData?.unlimitedInventory
            ? Constants.Unlimited
            : formData?.quotaDigitalCard || '',
          onChange: handleQuotaDigitalCardChange,
          placeholderText: 'Add Quota',
          disabled: isQuotaDigitalCardDisabled,
          maxLength: 6,
          error: quotaDigitalCardValidationError
        })}

        {renderTextInput({
          label: 'Leftover Inventory - Digital Card',
          value: formData?.leftOverInventoryDigitalCard,
          onChange: handleLeftOverInventoryDigitalCardChange,
          placeholderText: 'Leftover Inventory',
          disabled: true
        })}

        {renderTextInput({
          label: 'Limit per Store - Digital Card',
          value: formData?.limitPerStoreDigitalCard,
          onChange: handleLimitPerStoreDigitalCardChange,
          placeholderText: 'Limit Account',
          maxLength: 3,
          error: limitPerStoreDigitalCardValidationError
        })}
      </div>

      <div className='flex flex-col items-start gap-6 md:flex-row'>
        {renderTextInput({
          id: 'quota-physical-card',
          label: 'Quota Physical Card*',
          value: formData?.unlimitedInventory
            ? Constants.Unlimited
            : formData?.quotaPhysicalCard || '',
          onChange: handleQuotaPhysicalCardChange,
          placeholderText: 'Add Quota',
          disabled: isQuotaPhysicalCardDisabled,
          maxLength: 6,
          error: quotaPhysicalCardValidationError
        })}

        {renderTextInput({
          label: 'Leftover Inventory - Physical Card',
          value: formData?.leftOverInventoryPhysicalCard,
          onChange: handleLeftOverInventoryPhysicalCardChange,
          placeholderText: 'Leftover Inventory',
          disabled: true
        })}

        {renderTextInput({
          label: 'Limit per Store - Physical Card',
          value: formData?.limitPerStorePhysicalCard,
          onChange: handleLimitPerStorePhysicalCardChange,
          placeholderText: 'Limit Account',
          maxLength: 3,
          error: limitPerStorePhysicalCardValidationError
        })}
      </div>
    </div>
  );
};

export default InventoryFieldsRowForCardTypeBoth;

InventoryFieldsRowForCardTypeBoth.propTypes = {
  formData: PropTypes.object.isRequired,
  handleQuotaDigitalCardChange: PropTypes.func.isRequired,
  handleLeftOverInventoryDigitalCardChange: PropTypes.func.isRequired,
  handleLimitPerStoreDigitalCardChange: PropTypes.func.isRequired,
  handleQuotaPhysicalCardChange: PropTypes.func.isRequired,
  handleLeftOverInventoryPhysicalCardChange: PropTypes.func.isRequired,
  handleLimitPerStorePhysicalCardChange: PropTypes.func.isRequired,
  isQuotaDigitalCardDisabled: PropTypes.bool.isRequired,
  isQuotaPhysicalCardDisabled: PropTypes.bool.isRequired,
  quotaDigitalCardValidationError: PropTypes.string,
  quotaPhysicalCardValidationError: PropTypes.string,
  limitPerStoreDigitalCardValidationError: PropTypes.string,
  limitPerStorePhysicalCardValidationError: PropTypes.string
};
